import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, UserRoundCog, EyeOff, Timer, Globe, Sparkles,
  ChevronLeft, ChevronRight, Check,
} from 'lucide-react';

/**
 * PrivacyWelcomeSlides — a lightweight 5-slide tour that introduces the
 * privacy uplift features to existing members on their first login after
 * the migration. Illustrated with lucide icons only (no video, no heavy
 * assets) to respect data-conscious users.
 */
const SLIDES = [
  {
    key: 'intro',
    icon: Sparkles,
    tint: 'from-emerald-500 to-blue-500',
    title: 'Your privacy just got quieter',
    body: (
      <>
        We added five small things that keep MyInvestFolio out of the eye of anyone
        looking over your shoulder — on your phone, laptop, or a shared screen.
        <br /><br />
        Take 30 seconds to meet them.
      </>
    ),
  },
  {
    key: 'code-name',
    icon: UserRoundCog,
    tint: 'from-emerald-500 to-emerald-700',
    title: 'You now have a Code Name',
    body: (
      <>
        Instead of your real name, the app greets you with a two-word alias
        like <strong>COPPER BEACON</strong>. Screenshots, exports and browser
        history all use it — never your real name.
        <br /><br />
        You can change it any time in your Privacy tab.
      </>
    ),
  },
  {
    key: 'hide-amounts',
    icon: EyeOff,
    tint: 'from-slate-500 to-slate-700',
    title: 'Hide every figure with one tap',
    body: (
      <>
        Tap the little eye in the header and every dollar amount, percentage
        and balance becomes dots. Structure and layout stay put — only the
        numbers hide.
        <br /><br />
        Perfect for airports, cafés, and video calls.
      </>
    ),
  },
  {
    key: 'auto-lock',
    icon: Timer,
    tint: 'from-blue-500 to-blue-700',
    title: 'Auto-Lock after 5 quiet minutes',
    body: (
      <>
        Step away from your desk? After five minutes of no activity, the
        screen fogs over and asks for your password to come back.
        <br /><br />
        Always on. You don&apos;t have to remember to lock anything.
      </>
    ),
  },
  {
    key: 'browser-disguise',
    icon: Globe,
    tint: 'from-purple-500 to-purple-700',
    title: 'Browser tab in disguise',
    body: (
      <>
        In someone else&apos;s browser history, your tab can read &ldquo;Reading List&rdquo;
        or &ldquo;Notes&rdquo; instead of MyInvestFolio. Pick the disguise you like in
        the Privacy tab.
      </>
    ),
  },
  {
    key: 'privacy-mode',
    icon: ShieldCheck,
    tint: 'from-emerald-600 to-emerald-800',
    title: 'One master switch: Privacy Mode',
    body: (
      <>
        Feel someone watching? Flip <strong>Privacy Mode</strong> and all of
        the above turn on together — dots on numbers, disguised tab, header
        reads &ldquo;MEMBER&rdquo;. Your password unlocks it again.
        <br /><br />
        Everything lives in your new <strong>Privacy</strong> tab.
      </>
    ),
  },
];

const dismiss = (setOpen, onFinish) => {
  try { localStorage.setItem('mif:privacyWelcomeSeen', '1'); } catch { /* private mode */ }
  setOpen(false);
  onFinish?.();
};

const PrivacyWelcomeSlides = ({ isOpen, onClose, onGoToPrivacy }) => {
  const [step, setStep] = useState(0);
  const slide = SLIDES[step];
  const Icon = slide.icon;
  const isLast = step === SLIDES.length - 1;

  const handleClose = () => dismiss(onClose, null);
  const handleGo = () => dismiss(onClose, onGoToPrivacy);

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        data-testid="privacy-welcome-slides"
        className="sm:max-w-lg max-w-[95vw] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${slide.tint} flex items-center justify-center mb-3 shadow-lg`}>
            <Icon className="w-8 h-8 text-white" aria-hidden="true" />
          </div>
          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
            {step === 0 ? 'What\'s new' : `Feature ${step} of ${SLIDES.length - 1}`}
          </p>
          <DialogTitle className="text-xl sm:text-2xl font-serif text-slate-900 leading-tight">
            {slide.title}
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm sm:text-base text-slate-600 leading-relaxed py-2" data-testid={`slide-body-${slide.key}`}>
          {slide.body}
        </div>

        <div className="flex items-center justify-center gap-1.5 py-2">
          {SLIDES.map((s, idx) => (
            <div
              key={s.key}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                idx === step ? 'w-8 bg-gradient-to-r from-emerald-500 to-emerald-600'
                  : idx < step ? 'w-2 bg-emerald-400' : 'w-2 bg-slate-300'
              }`}
            />
          ))}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={handleClose}
            data-testid="privacy-welcome-skip"
            className="w-full sm:w-auto order-3 sm:order-1 text-xs text-slate-500 hover:text-slate-800"
          >
            Skip
          </Button>
          <Button
            variant="outline"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            data-testid="privacy-welcome-prev"
            className="w-full sm:w-auto order-2 disabled:opacity-40 text-xs sm:text-sm"
          >
            <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Back
          </Button>
          {isLast ? (
            <Button
              onClick={handleGo}
              data-testid="privacy-welcome-go-to-privacy"
              className="w-full sm:w-auto order-1 sm:order-3 bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm"
            >
              <Check className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Open Privacy tab
            </Button>
          ) : (
            <Button
              onClick={() => setStep((s) => Math.min(SLIDES.length - 1, s + 1))}
              data-testid="privacy-welcome-next"
              className="w-full sm:w-auto order-1 sm:order-3 bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm"
            >
              Next <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyWelcomeSlides;
